import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { deleteItem, fetchItems, postItem, putItem } from '../support/queries';

/** Where an item on the Enablement table comes from; the id spaces differ, so rows are keyed by both. */
export type EnablementMetaSource = 'journey_map' | 'education_series';

/** Details an admin enters for an item whose source does not carry them (`db/enablement-item-meta`). */
export interface EnablementItemMeta {
  id: number;
  source: EnablementMetaSource;
  source_id: string;
  name: string;
  /** YYYY-MM-DD, or null when only a note is stored. */
  due_date: string | null;
  notes: string;
  last_updated?: string;
  updated_by?: string | null;
}

const KEY = ['enablement-item-meta'];

/** `journey_map:12` / `education_series:abc`, the lookup key for one item. */
export const enablementMetaKey = (source: EnablementMetaSource, sourceId: string | number) =>
  `${source}:${sourceId}`;

export const indexEnablementMeta = (rows: EnablementItemMeta[] | undefined) => {
  const out = new Map<string, EnablementItemMeta>();
  for (const r of rows ?? []) out.set(enablementMetaKey(r.source, r.source_id), r);
  return out;
};

/** Every admin-entered detail. Everyone can read them: the profile table uses the due dates. */
export function useEnablementItemMeta(enabled = true) {
  return useQuery<EnablementItemMeta[]>({
    queryKey: KEY,
    queryFn: async () => {
      const data = await fetchItems('db/enablement-item-meta/');
      return Array.isArray(data) ? (data as EnablementItemMeta[]) : [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export interface SaveMetaInput {
  existing?: EnablementItemMeta | null;
  source: EnablementMetaSource;
  source_id: string;
  name: string;
  due_date: string | null;
  notes?: string;
}

/** Creates, updates or (when the date and note are both empty) removes one item's details. */
export function useSaveEnablementItemMeta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveMetaInput) => {
      const notes = (input.notes ?? input.existing?.notes ?? '').trim();
      if (!input.due_date && !notes) {
        if (input.existing?.id) await deleteItem(`db/enablement-item-meta/${input.existing.id}/`);
        return null;
      }
      const body = {
        source: input.source,
        source_id: input.source_id,
        name: input.name,
        due_date: input.due_date || null,
        notes,
      };
      return input.existing?.id
        ? putItem('db/enablement-item-meta', { ...body, id: input.existing.id })
        : postItem('db/enablement-item-meta/', body);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
